// tog'a #3835 (2026-10-07): «AI ning o'zida barcha userlar uchun limit berishni qo'sh, filtr ham qo'y tepaga».
// Bosh admin — barcha tizimlar xodimlari (SAV + magazinlar + kontragentlar); boshqa admin — faqat o'z tizimi (server ham shunday cheklaydi).
// Tepada filtr (tur, tizim, qidiruv, holat) → filtrdagi hammaga bitta oylik limit / limitni olib tashlash / AI yoqish-o'chirish.
// Server: AIAgent/Users?all, AIAgent/Billing/SetQuota(Bulk), AIAgent/ToggleUser, AIAgent/UsersAiBulk (har ommaviy amal — amallar jurnalida).
import React, { useMemo, useState } from "react"
import { Button, Input, InputNumber, Popconfirm, Radio, Select, Space, Switch, Tag, Typography, message } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { request } from "../../utils/config"
import i18n from "../../services/i18n"
import ResponsiveTable from "../table/ResponsiveTable"

type AiUser = {
  userId: number; email?: string; userName?: string; aiEnabled: boolean; monthlyLimit: number; usedThisMonth: number; callsThisMonth?: number
  tenantId: number; tenantName: string; tenantType: string; tenantAiEnabled: boolean; isSelf?: boolean
}

const fmt = (n: any) => Number(n || 0).toLocaleString("ru-RU")
const errText = (e: any) => e?.response?.data?.message || e?.message || i18n.t("error")
const TYPE_TAG: Record<string, { color: string; key: string }> = {
  main: { color: "blue", key: "aiuTypeMain" },
  shop_owner: { color: "purple", key: "aiuTypeShop" },
  counterparty: { color: "cyan", key: "aiuTypeCt" },
}
const numFormatter = (v: any) => `${v ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
const numParser = (v: any) => Number(String(v || "").replace(/\s/g, "")) as any

export default function AiUsersAdmin({ allTenants }: { allTenants: boolean }) {
  const qc = useQueryClient()
  const [type, setType] = useState("all")
  const [tenant, setTenant] = useState<number | undefined>()
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("all")
  const [limit, setLimit] = useState<number | null>(300000)
  const [busy, setBusy] = useState(false)

  const { data, isLoading } = useQuery(["AI_USERS", allTenants], async () =>
    (await request({ url: "AIAgent/Users", params: allTenants ? { all: true } : {} })).data as AiUser[])
  const rows: AiUser[] = useMemo(() => (Array.isArray(data) ? data : []), [data])   // har renderda yangi [] bo'lib useMemo'larni qayta hisoblatmasin
  const refresh = () => qc.invalidateQueries(["AI_USERS"])

  const tenantOptions = useMemo(() => {
    const m = new Map<number, string>()
    rows.filter((r) => type === "all" || r.tenantType === type).forEach((r) => m.set(r.tenantId, r.tenantName))
    return Array.from(m.entries()).map(([value, label]) => ({ value, label }))
  }, [rows, type])

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase()
    return rows.filter((r) =>
      (type === "all" || r.tenantType === type) &&
      (!tenant || r.tenantId === tenant) &&
      (!s || `${r.userName || ""} ${r.email || ""}`.toLowerCase().includes(s)) &&
      (status === "all" ||
        (status === "limited" && r.monthlyLimit > 0) ||
        (status === "unlimited" && !(r.monthlyLimit > 0)) ||
        (status === "off" && !r.aiEnabled) ||
        (status === "spent" && r.monthlyLimit > 0 && r.usedThisMonth >= r.monthlyLimit)))
  }, [rows, type, tenant, search, status])
  const ids = filtered.map((r) => r.userId)
  const n = ids.length

  const bulk = async (url: string, payload: any) => {
    setBusy(true)
    try {
      const r = await request({ url, method: "post", data: payload })
      message.success(i18n.t("aiuDone", { n: r.data?.updated ?? 0 }))
      refresh()
    } catch (e) { message.error(errText(e)) } finally { setBusy(false) }
  }
  const setOne = async (userId: number, monthlyLimit: number) => {
    try {
      await request({ url: "AIAgent/Billing/SetQuota", method: "post", data: { userId, monthlyLimit } })
      message.success(i18n.t("aiQuotaSaved")); refresh()
    } catch (e) { message.error(errText(e)) }
  }
  const toggle = async (userId: number) => {
    try { await request({ url: "AIAgent/ToggleUser", method: "post", params: { userId } }); refresh() }
    catch (e) { message.error(errText(e)) }
  }

  const columns: any[] = [
    {
      title: i18n.t("aiuColUser"), dataIndex: "userName", key: "user",
      render: (_: any, r: AiUser) => (
        <div>
          <b>{r.userName || r.email || `#${r.userId}`}</b>{r.isSelf ? <Tag style={{ marginLeft: 6 }}>{i18n.t("aiuYou")}</Tag> : null}
          {r.email && r.email !== r.userName ? <div style={{ fontSize: 12, color: "#64748b" }}>{r.email}</div> : null}
        </div>
      ),
    },
    ...(allTenants ? [{
      title: i18n.t("aiuColSystem"), dataIndex: "tenantName", key: "tenant", width: 190,
      render: (_: any, r: AiUser) => {
        const t = TYPE_TAG[r.tenantType] || TYPE_TAG.counterparty
        return (
          <div>
            <div>{r.tenantName}</div>
            <Tag color={t.color} style={{ marginTop: 2 }}>{i18n.t(t.key)}</Tag>
            {!r.tenantAiEnabled ? <div style={{ fontSize: 11, color: "#dc2626" }}>{i18n.t("aiuSystemOff")}</div> : null}
          </div>
        )
      },
    }] : []),
    {
      title: "AI", dataIndex: "aiEnabled", key: "ai", width: 70, align: "center" as const,
      render: (v: boolean, r: AiUser) => <Switch size="small" checked={v} disabled={r.isSelf} onChange={() => toggle(r.userId)} />,
    },
    {
      title: i18n.t("aiuColLimit"), dataIndex: "monthlyLimit", key: "limit", width: 150,
      render: (v: number, r: AiUser) => (
        <InputNumber key={`${r.userId}-${v}`} size="small" min={0} step={10000} style={{ width: 130 }}
          defaultValue={v > 0 ? v : undefined} placeholder={i18n.t("aiuUnlimited")} formatter={numFormatter} parser={numParser}
          onPressEnter={(e: any) => { const x = numParser(e.target.value) || 0; if (x !== (v || 0)) setOne(r.userId, x) }}
          onBlur={(e: any) => { const x = numParser(e.target.value) || 0; if (x !== (v || 0)) setOne(r.userId, x) }} />
      ),
    },
    {
      title: i18n.t("aiuColUsed"), dataIndex: "usedThisMonth", key: "used", width: 170,
      sorter: (a: AiUser, b: AiUser) => (a.usedThisMonth || 0) - (b.usedThisMonth || 0),
      render: (v: number, r: AiUser) => {
        const lim = r.monthlyLimit || 0
        const color = lim > 0 && v >= lim ? "#dc2626" : lim > 0 && v >= lim * 0.8 ? "#d97706" : undefined
        return (
          <div>
            <span style={{ color, fontWeight: color ? 700 : 400 }}>{fmt(v)}</span>
            {lim > 0 ? <span style={{ color: "#64748b" }}> / {fmt(lim)}</span> : null}
            {r.callsThisMonth ? <div style={{ fontSize: 11, color: "#94a3b8" }}>{i18n.t("aiuCalls", { n: r.callsThisMonth })}</div> : null}
          </div>
        )
      },
    },
  ]

  return (
    <div data-testid="ai-users-admin">
      <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>👥 {i18n.t("aiuTitle")}</Typography.Text>
      {/* filtr — tepada (tog'a #3835) */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        {allTenants && (
          <Radio.Group size="small" optionType="button" buttonStyle="solid" value={type} data-testid="aiu-type"
            onChange={(e) => { setType(e.target.value); setTenant(undefined) }}
            options={[
              { value: "all", label: i18n.t("aiuTypeAll") },
              { value: "main", label: i18n.t("aiuTypeMain") },
              { value: "shop_owner", label: i18n.t("aiuTypeShop") },
              { value: "counterparty", label: i18n.t("aiuTypeCt") },
            ]} />
        )}
        {allTenants && (
          <Select size="small" allowClear showSearch optionFilterProp="label" placeholder={i18n.t("aiuSystem")} value={tenant}
            onChange={(v) => setTenant(v as any)} options={tenantOptions} style={{ minWidth: 180 }} data-testid="aiu-tenant" />
        )}
        <Input size="small" allowClear prefix={<SearchOutlined style={{ color: "#94a3b8" }} />} placeholder={i18n.t("aiuSearch")}
          value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 220 }} data-testid="aiu-search" />
        <Select size="small" value={status} onChange={setStatus} style={{ width: 170 }} data-testid="aiu-status"
          options={[
            { value: "all", label: i18n.t("aiuStatusAll") },
            { value: "limited", label: i18n.t("aiuStatusLimited") },
            { value: "unlimited", label: i18n.t("aiuStatusUnlimited") },
            { value: "spent", label: i18n.t("aiuStatusSpent") },
            { value: "off", label: i18n.t("aiuStatusOff") },
          ]} />
      </div>

      {/* filtrdagi hammaga birdan */}
      <div style={{ background: "#f6f8fc", border: "1px solid #e5e9f2", borderRadius: 10, padding: 10, marginBottom: 10 }} data-testid="aiu-bulk">
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{i18n.t("aiuFiltered", { n })}</div>
        <Space wrap size={6}>
          <InputNumber min={0} step={10000} value={limit as any} onChange={(v) => setLimit(v as any)} formatter={numFormatter} parser={numParser}
            style={{ width: 170 }} placeholder={i18n.t("aiuLimitPh")} data-testid="aiu-limit" />
          {[100000, 300000, 1000000].map((x) => (
            <Button key={x} size="small" onClick={() => setLimit(x)}>{x >= 1000000 ? x / 1000000 + "M" : x / 1000 + "k"}</Button>
          ))}
          <Popconfirm title={i18n.t("aiuApplyConfirm", { n, limit: fmt(limit) })} disabled={!n || !(Number(limit) > 0)}
            onConfirm={() => bulk("AIAgent/Billing/SetQuotaBulk", { userIds: ids, monthlyLimit: Number(limit) || 0 })}>
            <Button type="primary" loading={busy} disabled={!n || !(Number(limit) > 0)} data-testid="aiu-apply">{i18n.t("aiuApply", { n })}</Button>
          </Popconfirm>
          <Popconfirm title={i18n.t("aiuRemoveConfirm", { n })} disabled={!n}
            onConfirm={() => bulk("AIAgent/Billing/SetQuotaBulk", { userIds: ids, monthlyLimit: 0 })}>
            <Button loading={busy} disabled={!n} data-testid="aiu-remove">{i18n.t("aiuRemove")}</Button>
          </Popconfirm>
          <Popconfirm title={i18n.t("aiuOnConfirm", { n })} disabled={!n} onConfirm={() => bulk("AIAgent/UsersAiBulk", { userIds: ids, enabled: true })}>
            <Button loading={busy} disabled={!n} data-testid="aiu-on">{i18n.t("aiuOn")}</Button>
          </Popconfirm>
          <Popconfirm title={i18n.t("aiuOffConfirm", { n })} disabled={!n} onConfirm={() => bulk("AIAgent/UsersAiBulk", { userIds: ids, enabled: false })}>
            <Button danger loading={busy} disabled={!n} data-testid="aiu-off">{i18n.t("aiuOff")}</Button>
          </Popconfirm>
        </Space>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>{i18n.t("aiuHint")}</div>
      </div>

      <ResponsiveTable rowKey="userId" size="small" loading={isLoading} dataSource={filtered} columns={columns}
        pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }} scroll={{ x: allTenants ? 760 : 560 }}
        locale={{ emptyText: i18n.t("nothingFound") }} />
    </div>
  )
}
